import React, {useCallback, useState} from 'react';
import {SubModelActionProps} from '../../../actionMenuTypes';
import {useDispatch} from 'react-redux';
import {scenarioLoadList} from '../../../../../store/scenario';
import {Checkbox, FormControlLabel} from '@mui/material';
import {tid} from '../../../../../testUtils';
import CancelButton from '../../../../core/Buttons/CancelButton';
import SubmitButton from '../../../../core/Buttons/SubmitButton';
import {TrashCan16} from '@carbon/icons-react';
import {SUBMODEL_ACTION_MENU_IDS} from '../../../../../test/types';
import {executionSetupDelete, typeToSection} from '../../../../../store/executionSetup';
import {messageAdd, MessageTypes} from '../../../../../store/message';
import styles from '../../../../core/Form/CheckboxControl.module.scss';
import {useMounted} from '../../../../../hooks/useMounted';
import {trackSubModelDeleted} from '../../../../../tracking/tracking';
import {getUrlRoute, MRAppRoutes, useRoute} from '../../../../router/routes';
import {useNavigate} from 'react-router-dom';
import {useLastValidValue} from '../../../../../hooks/useLastValidValue';
import {useModelInput} from '../../../../../store/modelInputs';
import {useAuth} from '../../../../../store/auth';
import {ScenarioOrderBy} from 'hemwb-api';
import {fullName} from '../../../../../store/user/utils';

type SubModelDeleteCheckResult = {
  canDelete: boolean;
  otherUsersInstanceCount: number;
  otherUserNames: string[];
};

const SubModelDelete: React.FC<SubModelActionProps> = ({model, subModel: _subModel, onSuccess, onCancel}) => {
  const dispatch = useDispatch();
  const mounted = useMounted();
  const navigate = useNavigate();
  const subModel = useLastValidValue(_subModel);
  const {auth} = useAuth();
  const user = auth.loggedInUser;

  const {subModelId} = useRoute<MRAppRoutes.MODEL_INPUT_EDITOR>();

  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const input = useModelInput(Number(subModel.id));

  const canAbletoDeleteSubModel = useCallback(async (): Promise<SubModelDeleteCheckResult> => {
    const data = await scenarioLoadList(dispatch, {
      modelUuid: model.uuid,
      modelInputId: input?.id,
      pageSize: 20,
      pageIndex: 0,
      orderBy: ScenarioOrderBy.CreatedOn,
      orderDesc: true,
      view: true,
    });

    if (!data) {
      return {
        canDelete: false,
        otherUsersInstanceCount: 0,
        otherUserNames: [],
      };
    }

    const instancesCreatedByOtherUsers = (data.list || []).filter(
      (scenario) => !!scenario.createdBy?.id && scenario.createdBy.id !== user?.id,
    );
    const otherUserNames = Array.from(
      new Set(instancesCreatedByOtherUsers.map((scenario) => fullName(scenario.createdBy)).filter((name) => !!name)),
    );

    return {
      canDelete: instancesCreatedByOtherUsers.length === 0,
      otherUsersInstanceCount: instancesCreatedByOtherUsers.length,
      otherUserNames,
    };
  }, [dispatch, input?.id, model.uuid, user?.id]);

  const handleSubmit = useCallback(async () => {
    setSubmitting(true);

    try {
      if (Number(subModelId) === subModel.id) {
        navigate(
          getUrlRoute(MRAppRoutes.SUBMODEL_LIST, {modelUuid: model.uuid, section: typeToSection(subModel.type)}),
        );
      }
      const deleteCheck = await canAbletoDeleteSubModel();

      if (deleteCheck.canDelete) {
        await executionSetupDelete(dispatch, subModel.id);
        trackSubModelDeleted(model, subModel);
        dispatch(messageAdd('Sub-model has been deleted', MessageTypes.SUCCESS));
      } else if (deleteCheck.otherUsersInstanceCount > 0 && deleteCheck.otherUserNames.length > 0) {
        dispatch(
          messageAdd(
            `You cannot delete this sub-model because ${
              deleteCheck.otherUsersInstanceCount
            } instances created by other users exist: ${deleteCheck.otherUserNames.join(', ')}.`,
            MessageTypes.ERROR,
          ),
        );
      } else {
        dispatch(messageAdd('Sub-model has not been deleted', MessageTypes.ERROR));
      }
      mounted.current && setSubmitting(false);
      onSuccess?.();
    } catch (e) {
      setSubmitting(false);
    }
  }, [canAbletoDeleteSubModel, dispatch, navigate, model, subModel, mounted, onSuccess, subModelId]);

  return (
    <>
      <h2>Delete sub-model</h2>
      <FormControlLabel
        classes={{root: styles.checkboxTopLabelRoot}}
        control={
          <Checkbox
            checked={confirmed}
            classes={{root: styles.checkboxTopCheckboxRoot}}
            color="secondary"
            onChange={() => setConfirmed(!confirmed)}
            name="confirm"
            {...tid('checkbox-option')}
          />
        }
        label="I confirm that this sub-model will be permanently deleted."
      />

      <div className="buttonsContainer">
        <br />
        <br />
        <CancelButton onClick={onCancel} className="redButton" {...tid(SUBMODEL_ACTION_MENU_IDS.DELETE, 'cancel')} />
        <SubmitButton
          onClick={handleSubmit}
          active={submitting}
          disabled={!confirmed}
          className="redButton"
          endIcon={<TrashCan16 />}
          {...tid(SUBMODEL_ACTION_MENU_IDS.DELETE, 'submit')}
        >
          Delete sub-model
        </SubmitButton>
      </div>
    </>
  );
};

export default SubModelDelete;
